import toast from "react-hot-toast";
import Swal, { SweetAlertInput, SweetAlertOptions } from 'sweetalert2';
import BorderlessButton from "@shared/components/BorderlessButton";
import styled from "styled-components";
import { ISelectionListOption } from "@shared/interfaces/SelectionListOptions";

export const useAlert = () => {

  const toastError = (message: string, duration = 3000) => {
    toast.error(message, { duration });
  };

  const toastWarning = (message: string, duration = 3000) => {
    toast(message, {
      icon: '⚠️',
      duration
    });
  };

  const success = async (title: string, text: string = '') => {
    return await Swal.fire({
      title,
      text,
      icon: 'success',
      confirmButtonColor: '#2778c4',
      confirmButtonText: 'Confirm'
    });
  };

  const error = async (title: string, text: string = '') => {
    return await Swal.fire({
      title,
      text,
      icon: 'error',
      confirmButtonColor: '#2778c4',
      confirmButtonText: 'Confirm'
    });
  };

  const warning = async (title: string, text: string = '') => {
    return await Swal.fire({
      title,
      text,
      icon: 'warning',
      confirmButtonColor: '#2778c4',
      confirmButtonText: 'Confirm'
    });
  };

  const toastSuccess = (message: string) => {
    toast.success(message);
  };

  const info = async (title: string, text: string = '', allowOutsideClick: boolean = true) => {
    return await Swal.fire({
      title,
      text,
      icon: 'info',
      confirmButtonColor: '#2778c4',
      confirmButtonText: 'Confirm',
      allowOutsideClick
    });
  };

  const confirm = (title: string, text: string = '', confirmButtonText: string = 'Confirm', denyButtonText: string = 'Cancel') => {
    return new Promise(resolve => Swal.fire({
      title,
      text,
      showDenyButton: true,
      confirmButtonColor: '#2778c4',
      confirmButtonText,
      denyButtonText,
      icon: 'warning'
    }).then((result) => resolve(result.isConfirmed)));
  };

  const textarea = (title: string, confirmButtonText: string = 'Confirm', denyButtonText: string = 'Cancel', input: any = 'textarea') => {
    return new Promise<string>(resolve => Swal.fire({
      title,
      input,
      showDenyButton: true,
      confirmButtonColor: '#2778c4',
      confirmButtonText,
      denyButtonText,
      icon: 'info'
    }).then((result) => resolve(result.isConfirmed ? result.value : null)));
  };

  const waitForConnection = async (title: string, text: string = '', confirmButtonText: string = 'Confirm') => {
    return new Promise<boolean>(async (outerResolve) => {
      let isConnected = window.navigator.onLine;

      const initialAlertConfig = {
        title,
        text,
        showDenyButton: false,
        showConfirmButton: false,
        showCancelButton: false,
        confirmButtonColor: '#2778c4',
        confirmButtonText,
        icon: 'error',
        allowOutsideClick: false,
        allowEscapeKey: false
      } as SweetAlertOptions;

      if (!isConnected) {
        Swal.fire(initialAlertConfig).then((result) => {
          outerResolve(result.isConfirmed);
        });
      }

      const handleOnline = () => {
        const updatedAlertConfig = {
          ...initialAlertConfig,
          showConfirmButton: true,
          title: 'Connection recovered!',
          text: 'Press confirm to continue',
          icon: 'info'
        } as SweetAlertOptions;

        Swal.update(updatedAlertConfig);
        window.removeEventListener('online', handleOnline);
      };

      window.addEventListener('online', handleOnline);

      while (!isConnected) {
        await new Promise<void>((innerResolve) => {
          setTimeout(() => {
            innerResolve();
          }, 1000);
        });

        isConnected = window.navigator.onLine;
      }
    });
  };

  const input = (title: string, inputType: SweetAlertInput = 'text', confirmButtonText: string = 'Confirm', denyButtonText: string = 'Cancel', confirmButtonColor?: string) => {
    return new Promise<string>(resolve => Swal.fire({
      title,
      input: inputType,
      showDenyButton: true,
      confirmButtonColor: confirmButtonColor ?? '#F29D52',
      confirmButtonText,
      denyButtonText
    }).then((result) => resolve(result.isConfirmed ? result.value : null)));
  };

  const customToast = ({ id, render }) => {
    const CloseButton = styled.div`
      position: absolute;
      top: 3px;
      right: 3px;
    `;

    toast.custom(
      <div className={'position-relative'}>
        {render}
        <CloseButton>
          <BorderlessButton icon={'fa fa-times'} onClick={() => toast.dismiss(id)}></BorderlessButton>
        </CloseButton>
      </div>
      , { duration: 10000, id: id || 'custom-notification-identifier' });
  };

  const selectionList = (title: string, options: ISelectionListOption[], emptyMessage?: string): any => {
    return new Promise((resolve) => {
      let html = emptyMessage;
      if (options.length) html = '<div style="height:200px; overflow: auto">';

      options.forEach((group, index) => {
        html += `<div class="mb-1 mt-3" style="text-align:left;" > <strong>${group.name}</strong> </div><ul class="list-group">`;
        group.options.forEach((option, i) => {
          html += `<li class="list-group-item list-group-item-action cursor-pointer py-1"  id="option-${index}-${i}">${option}</li>`;
        });
        html += '</ul>';
      });

      html += '</div>';

      Swal.fire({
        title: title,
        html: html,
        showCancelButton: true,
        showConfirmButton: false,
        cancelButtonText: 'Cancel',
        didOpen: () => {
          options.forEach((group, index) => {
            group.options.forEach((option, i) => {
              document.getElementById(`option-${index}-${i}`).onclick = () => {
                resolve(option);
                Swal.close();
              };
            });
          });
        }
      }).then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          resolve(null);
        }
      });
    });
  };

  return {
    toastError,
    toastSuccess,
    confirm,
    textarea,
    info,
    success,
    input,
    error,
    warning,
    waitForConnection,
    customToast,
    toastWarning,
    selectionList
  };
};
